import React, { useRef } from "react";

import { Action, ContainerState, Element, EventContext, FieldMessages, PresenterContext, PresenterMessages, PresenterState, StructureHeading } from "./types";
import { DataMessages, DataState, useData } from "../core/hooks/useData";
import { broadcast, subscribe, unsubscribe, useChannel } from "../core/hooks/useChannel";
import { Container } from "./Container";
import { form_def, ref } from "../types";
import { newID } from "../core/utils/unique";
import { createDataEntryScriptEngine, DataEntryCustomScriptFunctions } from "./runScript";
import { InspectorContext } from "./Inspector";
import { getFirstObjectValue } from "../core/utils/objectPath";
import { PageTitleChannel, PageTitleMessages } from "../layout/PageTitle";
import { isArray } from "../core/utils/conversion";
import { report_def } from "../core/pdf/types";
import { ScriptEngine } from "../core/utils/script";
import { go } from "../core/route/Route";
import { get } from "http";

export function getPresenter(id: string) {
	const msg = {
		type: PresenterMessages.GET_STATE,
	};
	broadcast(id, msg);
	const presenter = (msg as any).state as PresenterState;
	return presenter;
}

// populate all subforms into places where they belong so that there is a single combined def of the form
export function prepareFormDef(def: Element, subForms?: { [k: string]: form_def }) {

	if (!def) return;
	if (!subForms) return;

	const containerTypes = [
		"container",
		"sub-form",
	]

	function scan(e: any) {
		if (!def.uniqueID) def.uniqueID = newID();

		if (e.type === "sub-form") {
			const sf = subForms![e.form] as any;
			if (sf) {
				e.elements = sf.data.elements ? sf.data.elements : [];

				// scan these subform contents also in case further references found
				for(let child of e.elements) {
					scan(child);
				}
			} else {
				e.elements = [];
			}
		}

		if (containerTypes.indexOf(e.type) >= 0) {
			for(let child of e.elements) {
				scan(child);
			}
		}
	}

	scan(def);

}

// focuses on the construction and interaction of form elements
// expects a surrounding data context to be present
export function  Presenter(props: {
	//form: account_form,
	formValues: any,

	def: Element,

	id?: string,

	title?: string,
	refs?: { [k: string]: ref },
	subForms?: { [k: string]: form_def },
	reports: { [k: string]: report_def },

	defaults?: any,

	options?: any,

	children?: any,

	designMode?: boolean,
	readOnly?: boolean,

	showSaveCancel?: boolean,

	onSetGeneratedData?: (name: string, data: any) => void,

	onSave?: (props: {
		title: string,
		formValues: any,
	}) => void,
}) {

	const { formValues, def, refs, subForms, reports, title } = props;

	const data = useData();

	data.value = formValues;

	const inspectorContext = React.useContext(InspectorContext);

	const context = React.useMemo(() => {
		const result = {

			channelID: props.id ? props.id : newID(),

			title,

			designMode: props.designMode,

			readOnly: props.readOnly,

			options: props.options ? props.options : {},

			defaults: props.defaults ? props.defaults : {},
			
			dataStates: {

			} as any,

			root: data,

			inspectorContext,

			structureHeadings: [] as StructureHeading[],

			withinEvent: {
				queuedEvents: [] as EventContext[],
			},

			scriptEngine: {} as ScriptEngine,

			triggerEvent: function(context: EventContext) {
				if (this.withinEvent.type) {
					// console.log(`Within ${this.withinEvent.type} event, queuing: ${context.type}`);
					// this.withinEvent.queuedEvents.push(context);
					return;
				}

				if (!context.def.events) return;

				const actions = context.def.events[context.type];
				if (!actions || actions.length <= 0) return;

				this.withinEvent.type = context.type;

				for(let a of actions) {
					this.runAction(context, a);
				}

				this.withinEvent.type = undefined;
				// const evts = this.withinEvent.queuedEvents.slice();
				// this.withinEvent.queuedEvents = [];

				// were any events triggered during the processing of this one, if so run them now
				// if (evts.length > 0) {
				// 	console.log(`Running ${evts.length} queued events`);
				// 	for(let e of evts) {
				// 		this.triggerEvent(e);
				// 	}	
				// }

			},

			setTitle: function(title: string) {
				this.title = title;

				// also update the page title
				broadcast(PageTitleChannel, {
					type: PageTitleMessages.SET_TITLE,
					title,
				});
			},

			setGeneratedData: function(name: string, data: any) {
				if (props.onSetGeneratedData) props.onSetGeneratedData(name, data);
			},

			getRefData: function(id: string) {

				function constructReturnValue(data: any) {
					// map from friendly name to underlying id of cols
					const map = {} as any;
					for(let c of data.columns) {
						map[c.title] = c.id;
					}

					const result = {

						getRow: (val: string, withinColName?: string) => {
							const id = map[withinColName ? withinColName : "ID"];
							for(let r of data.rows) {
								if (r[id] === val) {
									return r;
								}
							}
						},

						getRowVal: (r: any, prop: string) => {
							if (!r) return;
							return r[map[prop]];
						},

						...data,
						
					};

					return result;
				}


				if (refs) {
					const ref = refs![id];
					if (ref) return constructReturnValue(ref.data);	
				}

				// if we have inspector context then it will have all of the refTables
				// so try there as last resort
				if (this.inspectorContext && this.inspectorContext.refTables) {
					for(let ref of this.inspectorContext.refTables) {
						if (ref.id === id) {
							return constructReturnValue(ref.data);
						}
					}
				}

				return undefined;
			},
			getSubForm: function(id: string) {
				if (!subForms) return undefined;

				const subForm = subForms![id];
				return subForm.data;
			},
			getReport: function(id: string) {
				if (reports) {
					const ref = reports![id];
					if (ref) return ref.data;	
				}
				return undefined;
			},

			runAction: function(context: EventContext, a: Action) {
				if (a.type === "visibility" && a.options && a.options.criteria) {

					let visible = this.scriptEngine.run(a.options.criteria, context);

					const field = context.container.find(context.def.id);
					if (field) {
						broadcast(field.channelID, {
							type: FieldMessages.SET_VISIBILITY,
							visible,
						})
					} else {
						console.log("Field " + a.options.target + " not found");
					}
				
				} else if (a.type === "script" && a.options && a.options.script) {
					const script = a.options.script;
					this.scriptEngine.run(script, context);

				}
			},

			validateAll: function() {
				let ok = true;
				for(let m in this.dataStates) {
					const data = this.dataStates[m].data;
					data.validate();

					if (data.errors.length > 0) ok = false;
				}

				return ok;
			},

			// finds first matching value
			findValue: function(prop: string) {
				return getFirstObjectValue(data.value, prop);
			},

			// NOTE: expect any value to be in root of data context or parent data context
			getValue: function(
				prop: string,
				options: {
					dataState: DataState<any>,
					scope?: any,
					returnResultContext?: boolean,
				},
			) {
				let context = options.dataState ? options.dataState : this.root;

				// NOTE: while its expected a data context be provided it could just be standard JS object

				function searchContext(context: DataState<any>) {
					if (prop.indexOf(".") > 0) {
						const parts = prop.split(".");
						let o: any = context.value ? context.value : context;
						for(let p of parts) {
							o = o[p];
							if (o === undefined || o === null) return o;
						}
						return o;
					} else {
						if (context.value) {
							return context.value[prop];
						} else {
							return (context as any)[prop];
						}
					}	
				}

				while (context) {
					const result = searchContext(context);
					if (result !== undefined) {
						if (options.returnResultContext) {
							return {
								result,
								context,
							};
						} else {
							return result;
						}
					}
					
					context = context.parentContext!;
				}

				// check any available "data windows"
				if (options.scope && options.scope._dataWindows && options.scope._dataWindowsOrder) {
					// search in reverse order (most relevant to least)
					for(let i=options.scope._dataWindowsOrder.length-1;i>=0;i--) {
						const m = options.scope._dataWindowsOrder[i];
						const s = options.scope._dataWindows[m];

						const val = s ? searchContext(s) : undefined;
						if (val !== undefined) return val;
					}

					// for(let m in options.scope.dataWindows) {
					// 	const s = options.scope.dataWindows[m];
					// 	const val = searchContext(s);
					// 	if (val !== undefined) return val;
					// }
				}

				return undefined;
			},

			// NOTE: setValue is tricky
			// just for now only support setting of a value within current context (data state)
			// otherwise if the value didn't exist previously then how do you know whether 
			// it was intended to be set in this data state or some parent context?
			// ... plus just setting in this data state means the data.setValue can be used
			// which takes care of all of the related events / updating
			setValue: function(prop: string, value: any, startContext?: DataState<any>) {
				let context = startContext ? startContext : this.root;
				context.setValue(prop, value);
			},

			setFirstValue: function(prop: string, value: any) {
				// navigate through the def of the form to find corresponding object
				// could exist anywhere
				broadcast(this.channelID + "/all-fields", {
					type: PresenterMessages.SET_VALUE,
					prop,
					value,
				});
			},
	
			setChildValue: function(prop: string, value: any, startContainer: ContainerState) {
				// look through current container and children containers for the prop
				const f = startContainer.find(prop);
				if (!f) return;

				f.container.data.setValue(f.field.id, value);
			},

			registerDataState: function (data: DataState<any>) {
				this.dataStates[data.id] = {
					data,
					sub: subscribe(data.channelName, (msg: any) => {

						if (msg.type === DataMessages.CHANGE) {
							// tell all data states to re-validate
							this.validateAll();
						}
					}),
				};
			},
			unregisterDataState: function (data: DataState<any>) {
				const ref = this.dataStates[data.id];
				if (ref && ref.sub) unsubscribe(data.channelName, ref.sub);
				delete this.dataStates[data.id];
			},

			recalc: function (prop: string) {
				broadcast(this.channelID, {
					type: PresenterMessages.RECALC,
					prop,
				});
			},

		
		} as PresenterState;

		result.scriptEngine = createDataEntryScriptEngine(result);

		return result;

	}, [data, refs, subForms, title, inspectorContext, props.designMode, props.id]);

	React.useLayoutEffect(() => {
		// trigger initial validation to run all appropriate logic
		setTimeout(() => {
			// get root data context and trigger change on it
			const ch = context.rootContainer.data.channelName; //context.channelID
			broadcast(ch, {
				type: DataMessages.CHANGE,
				propsChanged: [],
			});
			broadcast(context.channelID, {
				type: PresenterMessages.READY,
				state: context,
			});
		}, 10);
	}, [context.channelID, context]);

	const recalcHandle = useRef<any>();

	useChannel(context.channelID, React.useCallback((msg) => {
		if (msg.type === PresenterMessages.GET_STATE) {
			msg.state = context;

		} else if (msg.type === PresenterMessages.SAVE) {
			if (props.readOnly || !props.onSave) return;
			
			// need to do full validation to see if anything still not valid
			// assume valid unless at least one fail comes back
			formValues.valid = true;
			for(const m in context.dataStates) {
				const data = context.dataStates[m];

				data.data.validate();
				if (data.data.hasErrors()) {
					formValues.valid = false;
					break;
				}

				// look for any array elements in the value and see if any objects there are flagged as incomplete
				// i.e. for parties[], shares[] etc
				for(let p in data.data.value) {
					const v = data.data.value[p];
					if (isArray(v)) {
						for(let item of v) {
							if (item.incomplete) {
								formValues.valid = false;
								break;
							}
						}
					}
				}

			}

			// trigger the before-save event
			broadcast(context.channelID, {
				type: PresenterMessages.BEFORE_SAVE,
			});

			props.onSave!({
				title: context.title, 
				formValues,
			});

		} else if (msg.type === PresenterMessages.RECALC_NEEDED) {
			// buffer this in case get a few in close proximity as visibilities change etc
			if (recalcHandle.current) clearTimeout(recalcHandle.current);
			recalcHandle.current = setTimeout(() => {
				broadcast(context.channelID, {
					type: PresenterMessages.RECALC,
				});
				// broadcast(context.channelID, {
				// 	type: DataMessages.CHANGE,
				// });
			}, 10);

		}

	}, [context]));

	if (props.defaults && context.defaults !== props.defaults) {
		context.defaults = props.defaults;
	}

	let saveCancelButtons;
	if (props.showSaveCancel !== false) {
		saveCancelButtons = 
			<div
				className="flex horiz gap-5 margin-top-20"
			>
				{
					!context.readOnly &&
					<button className="primary" onClick={() => {
						broadcast(context.channelID, {
							type: PresenterMessages.SAVE,
						});
					}}>Save</button>
				}
				<button
					className="flat"
					onClick={() => {
						go("/dashboard/");
					}}
				>Cancel</button>	
			</div>
		;
	}

	return (
		<PresenterContext.Provider value={context}>
			<div id={context.channelID} className={"presenter flex horiz relative " + (props.designMode ? "design-mode" : "")}>
				{props.children}
				<div className="fill-left fill" />
				<div className="root-container">
					<Container def={def} />

					{saveCancelButtons}
				</div>
				<div className="fill-right fill" />
			</div>

		</PresenterContext.Provider>
	);
}



